
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';

const TermsPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Update SEO meta tags based on current language
    const metaTitles = {
      de: 'Allgemeine Geschäftsbedingungen (AGB) | Future Level',
      en: 'Terms and Conditions | Future Level',
      fr: 'Conditions Générales | Future Level',
      it: 'Termini e Condizioni | Future Level'
    };
    
    const metaDescriptions = {
      de: 'Allgemeine Geschäftsbedingungen (AGB) der Future Level - KI-basierte Videomarketing Agentur in Emmen, Schweiz',
      en: 'Terms and Conditions of Future Level - AI-based video marketing agency in Emmen, Switzerland',
      fr: 'Conditions Générales de Future Level - Agence de marketing vidéo basée sur l\'IA à Emmen, Suisse',
      it: 'Termini e Condizioni di Future Level - Agenzia di marketing video basata sull\'IA a Emmen, Svizzera'
    };
    
    updateMetaTags({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      keywords: "AGB, Geschäftsbedingungen, Terms of Service, Future Level, Videoproduktion Bedingungen, Schweizer AGB",
      canonicalUrl: getCanonicalUrl(currentLanguage === 'de' ? 'agb' : 
                                   currentLanguage === 'en' ? 'terms' : 
                                   currentLanguage === 'fr' ? 'conditions' : 'termini', 
                                   currentLanguage),
      language: currentLanguage,
      ogType: "article"
    });
  }, [currentLanguage, t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8">{t.terms}</h1>
            
            {/* Swiss-law compliant Terms and Conditions content */}
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold mb-4">1. Allgemeine Geschäftsbedingungen (AGB)</h2>
              <p className="mb-4">
                Diese Allgemeinen Geschäftsbedingungen regeln das Vertragsverhältnis zwischen 
                der Future Level (nachfolgend "Anbieter") und ihren Kunden (nachfolgend "Kunde") 
                für sämtliche angebotenen Dienstleistungen im Bereich des KI-gestützten Videomarketings.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">2. Unternehmensdetails gemäß schweizerischem Recht</h2>
              <p className="mb-4">
                <strong>Unternehmensname:</strong> Future Level<br />
                <strong>Adresse:</strong> Meilipromenade 14, 6032 Emmen, Schweiz<br />
                <strong>E-Mail:</strong> info@future-level.ch<br />
                <strong>Telefon:</strong> +41 44 123 45 67<br />
                <strong>Unternehmensidentifikationsnummer:</strong> CHE-XXX.XXX.XXX
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">3. Vertragsabschluss</h2>
              <p className="mb-4">
                Der Vertrag zwischen dem Anbieter und dem Kunden kommt durch die schriftliche Bestätigung 
                eines Angebots oder Auftrags zustande. Als schriftliche Bestätigung gilt auch die Bestätigung 
                per E-Mail oder andere elektronische Kommunikationswege.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">4. Leistungsumfang</h2>
              <p className="mb-4">
                Der genaue Leistungsumfang wird im individuellen Angebot oder in der Auftragsbestätigung 
                festgehalten. Der Anbieter behält sich das Recht vor, zur Erfüllung seiner vertraglichen 
                Pflichten Dritte beizuziehen.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">5. Preise und Zahlungsbedingungen</h2>
              <p className="mb-4">
                Alle Preise verstehen sich in Schweizer Franken (CHF) und zuzüglich der gesetzlichen 
                Mehrwertsteuer. Rechnungen sind innerhalb von 30 Tagen nach Erhalt ohne Abzug zahlbar, 
                sofern nicht anders vereinbart.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">6. Nutzungsrechte</h2>
              <p className="mb-4">
                Mit vollständiger Bezahlung der vereinbarten Vergütung erhält der Kunde das Recht, 
                die erstellten Videoinhalte im vereinbarten Umfang zu nutzen. Die genauen Nutzungsrechte 
                werden im Einzelfall festgelegt.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">7. Haftungsbeschränkung</h2>
              <p className="mb-4">
                Der Anbieter haftet für Schäden nur bei Vorsatz oder grober Fahrlässigkeit. 
                Die Haftung für leichte Fahrlässigkeit ist ausgeschlossen. Die Haftung für indirekte 
                Schäden und Folgeschäden ist in jedem Fall ausgeschlossen, soweit dies gesetzlich zulässig ist.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">8. Datenschutz</h2>
              <p className="mb-4">
                Der Anbieter verpflichtet sich zur Einhaltung der geltenden datenschutzrechtlichen 
                Bestimmungen, insbesondere des Bundesgesetzes über den Datenschutz (DSG). 
                Weitere Informationen sind in der separaten <a href={`/${currentLanguage}/${currentLanguage === 'de' ? 'datenschutz' : currentLanguage === 'en' ? 'privacy-policy' : currentLanguage === 'fr' ? 'politique-de-confidentialite' : 'informativa-privacy'}`} className="text-primary hover:underline">Datenschutzerklärung</a> enthalten.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">9. Geheimhaltung</h2>
              <p className="mb-4">
                Beide Parteien verpflichten sich, alle vertraulichen Informationen, die ihnen im Rahmen 
                der Zusammenarbeit zugänglich werden, streng vertraulich zu behandeln und nicht an Dritte 
                weiterzugeben.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">10. Bestellprozess für E-Commerce</h2>
              <p className="mb-4">
                Bei Online-Bestellungen werden Kunden vor Abschluss der Bestellung über alle relevanten 
                Schritte informiert. Der Kunde erhält die Möglichkeit, seine Bestellung zu überprüfen und 
                Fehler zu korrigieren, bevor er eine verbindliche Bestellung abgibt. Nach Eingang der Bestellung 
                erhält der Kunde unverzüglich eine elektronische Bestätigung.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">11. Barrierefreiheit</h2>
              <p className="mb-4">
                Future Level bemüht sich, seine Online-Präsenz barrierefrei zu gestalten und orientiert sich 
                dabei an den WCAG 2.1 Level AA Standards, um die Zugänglichkeit für Personen mit Behinderungen 
                zu gewährleisten.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">12. Anwendbares Recht und Gerichtsstand</h2>
              <p className="mb-4">
                Diese AGB und alle Rechtsbeziehungen zwischen dem Anbieter und dem Kunden unterliegen 
                ausschließlich schweizerischem Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand für 
                alle Streitigkeiten aus oder im Zusammenhang mit diesen AGB ist Emmen, Schweiz.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">13. Änderungen der AGB</h2>
              <p className="mb-4">
                Der Anbieter behält sich das Recht vor, diese AGB jederzeit anzupassen. Die jeweils aktuelle 
                Version wird auf der Website veröffentlicht. Es gilt die zum Zeitpunkt des Vertragsabschlusses 
                gültige Version der AGB.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">14. Schlussbestimmungen</h2>
              <p className="mb-4">
                Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, so wird die Gültigkeit der 
                übrigen Bestimmungen davon nicht berührt. Anstelle der unwirksamen Bestimmung gilt eine 
                wirksame Bestimmung als vereinbart, die der von den Parteien gewollten wirtschaftlich am 
                nächsten kommt.
              </p>
              
              <p className="mt-8 text-sm text-gray-500">
                Stand: Mai 2025
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
